import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureRunPlanConditionREST} from 'procedures/network/procedureRunPlanDAO';
import {deriveConfirmationMethodByParamVersionId} from 'procedures/planning/buildFullExperimentPlan';
import {
  expandPlanDataParameterValuesForRest,
  expandPlanMaterialInputValuesForRest,
} from 'procedures/planning/multiValuePlateMap';
import {
  ExperimentalInputEvaluatedValues,
  ExperimentalInputs,
  ExperimentalInputsPerMethod,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {PlanConditionsByMethodId} from 'studies/typed/shared/procedure-runs/design-state/planConditionState';
import {
  Condition,
  ConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

const MATERIAL_INDEX = 0;
const VALUE_INDEX = 0;
const AMOUNT_INDEX = 1;

interface SerializePlanConditionsArgs {
  planConditionsByMethodId: PlanConditionsByMethodId;
  /** Flowchart method order. Each method's rows are emitted before the next method. */
  methodIdsInOrder: ReadonlyArray<string>;
  experimentalInputs: ExperimentalInputs;
  evaluatedValues: ExperimentalInputEvaluatedValues;
}

/**
 * Serializes per-method plan rows to the procedure run plan REST format.
 *
 * `position` is the row's array index within its method, including holes in a sparse array. A null
 * or missing row is left out of the payload, which deletes that row on save.
 */
export function serializePlanConditionsByMethodForNetwork({
  planConditionsByMethodId,
  methodIdsInOrder,
  experimentalInputs,
  evaluatedValues,
}: SerializePlanConditionsArgs): Array<ProcedureRunPlanConditionREST> {
  const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(experimentalInputs);
  const payloads: Array<ProcedureRunPlanConditionREST> = [];
  methodIdsInOrder.forEach((methodId) => {
    const rows = planConditionsByMethodId[methodId] ?? [];
    rows.forEach((row, position) => {
      if (row == null) {
        return;
      }
      const {id, name, description, numReplicates} = row;
      const inputs = experimentalInputs[methodId];
      assert(inputs != null, `Expected experimental inputs for method ${methodId}`);
      payloads.push({
        ...(id != null ? {id} : {}),
        templateMethodVersionId: methodId,
        name,
        description,
        position,
        replicateInfos: Array.from({length: numReplicates}, (_, replicateIndex) => ({
          replicateNumber: replicateIndex + 1,
        })),
        methodPlan: buildPlanMethodPlanRest(
          methodId,
          inputs,
          evaluatedValues[methodId]?.values[position],
          confirmationMethodByParamVersionId
        ),
      });
    });
  });
  return payloads;
}

/**
 * Builds one method plan from a method's parameter ids and a single condition's evaluated cells.
 */
export function buildPlanMethodPlanRest(
  methodId: string,
  inputsForMethod: ExperimentalInputsPerMethod,
  conditionValues: ExperimentalInputTypedValuesByComponentName | undefined,
  confirmationMethodByParamVersionId: Record<string, ProcedureParameterConfirmationMethod>
): NonNullable<ProcedureRunPlanConditionREST['methodPlan']> {
  return {
    methodVersionId: methodId,
    dataParameterValues: inputsForMethod.ids.parameters.map((paramVersionId, paramIdx) => ({
      dataParameterVersionId: paramVersionId,
      values: expandPlanDataParameterValuesForRest(conditionValues?.parameters[paramIdx]?.[VALUE_INDEX]),
    })),
    materialInputValues: inputsForMethod.ids.materialInputs.map((materialVersionId, materialIdx) => ({
      materialInputVersionId: materialVersionId,
      values: expandPlanMaterialInputValuesForRest(
        conditionValues?.materialInputs[materialIdx]?.[MATERIAL_INDEX],
        conditionValues?.materialInputs[materialIdx]?.[AMOUNT_INDEX],
        confirmationMethodByParamVersionId[materialVersionId]
      ),
    })),
    equipmentValues: inputsForMethod.ids.equipment.map((equipmentVersionId, equipmentIdx) => ({
      equipmentVersionId,
      // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
      // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
      // than a missing key.
      values: [
        {
          value: {
            equipment: conditionValues?.equipment[equipmentIdx]?.[VALUE_INDEX] ?? null,
          },
        },
      ],
    })),
  };
}

/**
 * Expands combined-table condition groups onto one row per method, so the save path can run on the
 * per-method map before plan state itself is per-method.
 *
 * An empty id map is an id-less row on every method. A populated id map must cover every method with
 * a distinct id. Asserting here keeps a sparse group from dropping rows on save.
 */
// TODO(BNCH-172699): Remove once plan state holds PlanConditionsByMethodId directly.
export function planConditionsFromConditionGroups(
  groups: ReadonlyArray<ConditionGroup>,
  methodIds: ReadonlyArray<string>
): PlanConditionsByMethodId {
  const planConditionsByMethodId: PlanConditionsByMethodId = Object.fromEntries(
    methodIds.map((methodId) => [methodId, [] as Array<Condition>])
  );
  groups.forEach((conditionGroup) => {
    const {name, description, numReplicates, conditionIdsByPmdvId} = conditionGroup;
    const isPersisted = Object.keys(conditionIdsByPmdvId).length > 0;
    const seenConditionIds = new Set<string>();
    methodIds.forEach((methodId) => {
      let id: string | undefined;
      if (isPersisted) {
        const conditionId = conditionIdsByPmdvId[methodId];
        assert(conditionId != null, `Expected condition ID for PMDV ${methodId} in conditionIdsByPmdvId`);
        assert(
          !seenConditionIds.has(conditionId),
          `Expected one condition ID per PMDV within a ConditionGroup, got duplicate condition ID ${conditionId}`
        );
        seenConditionIds.add(conditionId);
        id = conditionId;
      }
      planConditionsByMethodId[methodId].push({
        name,
        numReplicates,
        ...(description !== undefined ? {description} : {}),
        ...(id != null ? {id} : {}),
      });
    });
  });
  return planConditionsByMethodId;
}
